import * as React from 'react';

import { useToast } from '@rematter/ui-web/hooks';

import { useTranslations } from '~/app/i18n/useTranslations';
import { config } from '~/config';

// Check for version updates every 2 minutes
const CHECK_INTERVAL_MS = 2 * 60 * 1000;

type VersionManifest = {
  version: string;
};

export function useVersionMismatchNotification() {
  const toast = useToast();
  const t = useTranslations();
  const [hasShownToast, setHasShownToast] = React.useState(false);
  const [latestVersion, setLatestVersion] = React.useState<string | null>(null);

  const currentVersion = config.service.version;
  const isProduction = config.isProduction;

  const checkVersion = React.useCallback(async () => {
    try {
      const cacheBuster = Date.now();
      const response = await fetch(`/version.json?v=${cacheBuster}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000),
      });

      if (response.ok) {
        const manifest = (await response.json()) as VersionManifest;
        setLatestVersion(manifest.version);
      }
    } catch (error) {
      // Silently fail - don't interrupt user experience
      console.warn('Failed to check app version:', error);
    }
  }, []);

  React.useEffect(() => {
    if (!isProduction) {
      return;
    }

    checkVersion();

    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [checkVersion, isProduction]);

  React.useEffect(() => {
    if (
      !currentVersion ||
      !latestVersion ||
      currentVersion === latestVersion ||
      hasShownToast
    ) {
      return;
    }

    toast.info(t.versionMismatch.title, t.versionMismatch.description, {
      timeout: 0,
    });

    setHasShownToast(true);
  }, [currentVersion, latestVersion, hasShownToast, toast, t]);

  React.useEffect(() => {
    if (latestVersion && latestVersion !== currentVersion) {
      setHasShownToast(false);
    }
  }, [latestVersion, currentVersion]);
}
